// 中国主要城市经纬度数据
export interface City {
  name: string;
  longitude: number; // 经度（东经为正）
  latitude: number;  // 纬度（北纬为正）
  province: string;  // 省份
}

export interface CitySelectOption {
  label: string;
  value: string;
  city: City;
  searchText: string;
}

export interface CitySelectGroup {
  label: string;
  options: CitySelectOption[];
}

export type CityLocale = 'zh-CN' | 'en-US';

const PROVINCE_EN_MAP: Record<string, string> = {
  北京市: 'Beijing',
  上海市: 'Shanghai',
  天津市: 'Tianjin',
  重庆市: 'Chongqing',
  中国台湾: 'Taiwan, China',
  中国香港: 'Hong Kong, China',
  中国澳门: 'Macau, China',
  新加坡: 'Singapore',
  马来西亚: 'Malaysia',
  泰国: 'Thailand',
  日本: 'Japan',
  韩国: 'South Korea',
  美国: 'United States',
  加拿大: 'Canada',
  英国: 'United Kingdom',
  法国: 'France',
  德国: 'Germany',
  荷兰: 'Netherlands',
  澳大利亚: 'Australia',
  新西兰: 'New Zealand',
};

const CITY_EN_MAP: Record<string, string> = {
  北京: 'Beijing',
  上海: 'Shanghai',
  天津: 'Tianjin',
  重庆: 'Chongqing',
  广州: 'Guangzhou',
  深圳: 'Shenzhen',
  杭州: 'Hangzhou',
  南京: 'Nanjing',
  苏州: 'Suzhou',
  成都: 'Chengdu',
  武汉: 'Wuhan',
  西安: 'Xi’an',
  长沙: 'Changsha',
  郑州: 'Zhengzhou',
  青岛: 'Qingdao',
  厦门: 'Xiamen',
  福州: 'Fuzhou',
  昆明: 'Kunming',
  哈尔滨: 'Harbin',
  沈阳: 'Shenyang',
  大连: 'Dalian',
  台北: 'Taipei',
  新北: 'New Taipei',
  桃园: 'Taoyuan',
  台中: 'Taichung',
  台南: 'Tainan',
  高雄: 'Kaohsiung',
  基隆: 'Keelung',
  新竹: 'Hsinchu',
  嘉义: 'Chiayi',
  香港: 'Hong Kong',
  九龙: 'Kowloon',
  新界: 'New Territories',
  澳门: 'Macau',
  氹仔: 'Taipa',
  路环: 'Coloane',
  新加坡: 'Singapore',
  裕廊: 'Jurong',
  淡滨尼: 'Tampines',
  吉隆坡: 'Kuala Lumpur',
  槟城: 'Penang',
  新山: 'Johor Bahru',
  曼谷: 'Bangkok',
  清迈: 'Chiang Mai',
  东京: 'Tokyo',
  大阪: 'Osaka',
  京都: 'Kyoto',
  横滨: 'Yokohama',
  名古屋: 'Nagoya',
  首尔: 'Seoul',
  釜山: 'Busan',
  济州: 'Jeju',
  纽约: 'New York',
  洛杉矶: 'Los Angeles',
  旧金山: 'San Francisco',
  西雅图: 'Seattle',
  波士顿: 'Boston',
  芝加哥: 'Chicago',
  温哥华: 'Vancouver',
  多伦多: 'Toronto',
  蒙特利尔: 'Montreal',
  伦敦: 'London',
  曼彻斯特: 'Manchester',
  伯明翰: 'Birmingham',
  巴黎: 'Paris',
  里昂: 'Lyon',
  柏林: 'Berlin',
  慕尼黑: 'Munich',
  法兰克福: 'Frankfurt',
  阿姆斯特丹: 'Amsterdam',
  悉尼: 'Sydney',
  墨尔本: 'Melbourne',
  布里斯班: 'Brisbane',
  奥克兰: 'Auckland',
};

function getLocalizedProvinceName(province: string, locale: CityLocale) {
  if (locale === 'zh-CN') return province;
  return PROVINCE_EN_MAP[province] || province;
}

function getLocalizedCityName(name: string, locale: CityLocale) {
  if (locale === 'zh-CN') return name;
  return CITY_EN_MAP[name] || name;
}

export function getCityOptionValue(city: City) {
  return `${city.province}__${city.name}`;
}

export function findCityByOptionValue(value?: string) {
  if (!value) return undefined;
  return CITIES.find((city) => getCityOptionValue(city) === value);
}

export function isDomesticProvince(province: string) {
  return (
    province === '中国台湾' ||
    province === '中国香港' ||
    province === '中国澳门' ||
    province.endsWith('省') ||
    province.endsWith('市') ||
    province.endsWith('自治区') ||
    province.endsWith('兵团')
  );
}

export const CITIES: City[] = [
  // 直辖市
  { name: '北京', longitude: 116.4074, latitude: 39.9042, province: '北京市' },
  { name: '上海', longitude: 121.4737, latitude: 31.2304, province: '上海市' },
  { name: '天津', longitude: 117.2008, latitude: 39.0842, province: '天津市' },
  { name: '重庆', longitude: 106.5516, latitude: 29.5630, province: '重庆市' },

  // 河北省
  { name: '石家庄', longitude: 114.5149, latitude: 38.0428, province: '河北省' },
  { name: '唐山', longitude: 118.1752, latitude: 39.6304, province: '河北省' },
  { name: '秦皇岛', longitude: 119.6004, latitude: 39.9353, province: '河北省' },
  { name: '邯郸', longitude: 114.5391, latitude: 36.6256, province: '河北省' },
  { name: '邢台', longitude: 114.5042, latitude: 37.0682, province: '河北省' },
  { name: '保定', longitude: 115.4648, latitude: 38.8740, province: '河北省' },
  { name: '张家口', longitude: 114.8869, latitude: 40.8118, province: '河北省' },
  { name: '承德', longitude: 117.9634, latitude: 40.9518, province: '河北省' },
  { name: '沧州', longitude: 116.8387, latitude: 38.3037, province: '河北省' },
  { name: '廊坊', longitude: 116.6838, latitude: 39.5378, province: '河北省' },
  { name: '衡水', longitude: 115.6701, latitude: 37.7388, province: '河北省' },

  // 山西省
  { name: '太原', longitude: 112.5489, latitude: 37.8706, province: '山西省' },
  { name: '大同', longitude: 113.3002, latitude: 40.0766, province: '山西省' },
  { name: '阳泉', longitude: 113.5804, latitude: 37.8566, province: '山西省' },
  { name: '长治', longitude: 113.1163, latitude: 36.1953, province: '山西省' },
  { name: '晋城', longitude: 112.8513, latitude: 35.4903, province: '山西省' },
  { name: '朔州', longitude: 112.4328, latitude: 39.3313, province: '山西省' },
  { name: '晋中', longitude: 112.7537, latitude: 37.6872, province: '山西省' },
  { name: '运城', longitude: 111.0073, latitude: 35.0267, province: '山西省' },
  { name: '忻州', longitude: 112.7337, latitude: 38.4168, province: '山西省' },
  { name: '临汾', longitude: 111.5189, latitude: 36.0880, province: '山西省' },
  { name: '吕梁', longitude: 111.1341, latitude: 37.5178, province: '山西省' },

  // 内蒙古自治区
  { name: '呼和浩特', longitude: 111.6708, latitude: 40.8183, province: '内蒙古自治区' },
  { name: '包头', longitude: 109.8403, latitude: 40.6581, province: '内蒙古自治区' },
  { name: '乌海', longitude: 106.7947, latitude: 39.6551, province: '内蒙古自治区' },
  { name: '赤峰', longitude: 118.8870, latitude: 42.2587, province: '内蒙古自治区' },
  { name: '通辽', longitude: 122.2433, latitude: 43.6174, province: '内蒙古自治区' },
  { name: '鄂尔多斯', longitude: 109.7810, latitude: 39.6086, province: '内蒙古自治区' },
  { name: '呼伦贝尔', longitude: 119.7651, latitude: 49.2122, province: '内蒙古自治区' },
  { name: '巴彦淖尔', longitude: 107.3874, latitude: 40.7573, province: '内蒙古自治区' },
  { name: '乌兰察布', longitude: 113.1328, latitude: 41.0341, province: '内蒙古自治区' },
  { name: '兴安盟', longitude: 122.0378, latitude: 46.0763, province: '内蒙古自治区' },
  { name: '锡林郭勒盟', longitude: 116.0481, latitude: 43.9333, province: '内蒙古自治区' },
  { name: '阿拉善盟', longitude: 105.7286, latitude: 38.8448, province: '内蒙古自治区' },

  // 辽宁省
  { name: '沈阳', longitude: 123.4328, latitude: 41.8045, province: '辽宁省' },
  { name: '大连', longitude: 121.6147, latitude: 38.9140, province: '辽宁省' },
  { name: '鞍山', longitude: 122.9945, latitude: 41.1088, province: '辽宁省' },
  { name: '抚顺', longitude: 123.9571, latitude: 41.8800, province: '辽宁省' },
  { name: '本溪', longitude: 123.7655, latitude: 41.2986, province: '辽宁省' },
  { name: '丹东', longitude: 124.3543, latitude: 40.0005, province: '辽宁省' },
  { name: '锦州', longitude: 121.1268, latitude: 41.0956, province: '辽宁省' },
  { name: '营口', longitude: 122.2188, latitude: 40.6674, province: '辽宁省' },
  { name: '阜新', longitude: 121.6706, latitude: 42.0220, province: '辽宁省' },
  { name: '辽阳', longitude: 123.1723, latitude: 41.2694, province: '辽宁省' },
  { name: '盘锦', longitude: 122.0699, latitude: 41.1198, province: '辽宁省' },
  { name: '铁岭', longitude: 123.7260, latitude: 42.2236, province: '辽宁省' },
  { name: '朝阳', longitude: 120.4505, latitude: 41.5718, province: '辽宁省' },
  { name: '葫芦岛', longitude: 120.8371, latitude: 40.7113, province: '辽宁省' },

  // 吉林省
  { name: '长春', longitude: 125.3235, latitude: 43.8171, province: '吉林省' },
  { name: '吉林', longitude: 126.5492, latitude: 43.8378, province: '吉林省' },
  { name: '四平', longitude: 124.3505, latitude: 43.1666, province: '吉林省' },
  { name: '辽源', longitude: 125.1433, latitude: 42.8876, province: '吉林省' },
  { name: '通化', longitude: 125.9397, latitude: 41.7284, province: '吉林省' },
  { name: '白山', longitude: 126.4237, latitude: 41.9425, province: '吉林省' },
  { name: '松原', longitude: 124.8255, latitude: 45.1414, province: '吉林省' },
  { name: '白城', longitude: 122.8411, latitude: 45.6196, province: '吉林省' },
  { name: '延边', longitude: 129.5085, latitude: 42.8914, province: '吉林省' },

  // 黑龙江省
  { name: '哈尔滨', longitude: 126.5349, latitude: 45.8038, province: '黑龙江省' },
  { name: '齐齐哈尔', longitude: 123.9181, latitude: 47.3543, province: '黑龙江省' },
  { name: '鸡西', longitude: 130.9697, latitude: 45.2950, province: '黑龙江省' },
  { name: '鹤岗', longitude: 130.2977, latitude: 47.3502, province: '黑龙江省' },
  { name: '双鸭山', longitude: 131.1590, latitude: 46.6434, province: '黑龙江省' },
  { name: '大庆', longitude: 125.1032, latitude: 46.5896, province: '黑龙江省' },
  { name: '伊春', longitude: 128.8997, latitude: 47.7248, province: '黑龙江省' },
  { name: '佳木斯', longitude: 130.3184, latitude: 46.7995, province: '黑龙江省' },
  { name: '七台河', longitude: 131.0029, latitude: 45.7708, province: '黑龙江省' },
  { name: '牡丹江', longitude: 129.6337, latitude: 44.5521, province: '黑龙江省' },
  { name: '黑河', longitude: 127.5286, latitude: 50.2442, province: '黑龙江省' },
  { name: '绥化', longitude: 126.9686, latitude: 46.6372, province: '黑龙江省' },
  { name: '大兴安岭', longitude: 124.1965, latitude: 50.4085, province: '黑龙江省' },

  // 江苏省
  { name: '南京', longitude: 118.7969, latitude: 32.0603, province: '江苏省' },
  { name: '无锡', longitude: 120.3019, latitude: 31.5747, province: '江苏省' },
  { name: '徐州', longitude: 117.1849, latitude: 34.2618, province: '江苏省' },
  { name: '常州', longitude: 119.9740, latitude: 31.7728, province: '江苏省' },
  { name: '苏州', longitude: 120.5954, latitude: 31.2989, province: '江苏省' },
  { name: '南通', longitude: 120.8945, latitude: 31.9806, province: '江苏省' },
  { name: '连云港', longitude: 119.2216, latitude: 34.5968, province: '江苏省' },
  { name: '淮安', longitude: 119.0153, latitude: 33.6104, province: '江苏省' },
  { name: '盐城', longitude: 120.1625, latitude: 33.3798, province: '江苏省' },
  { name: '扬州', longitude: 119.4120, latitude: 32.3932, province: '江苏省' },
  { name: '镇江', longitude: 119.4252, latitude: 32.1877, province: '江苏省' },
  { name: '泰州', longitude: 119.9229, latitude: 32.4491, province: '江苏省' },
  { name: '宿迁', longitude: 118.2751, latitude: 33.9630, province: '江苏省' },

  // 浙江省
  { name: '杭州', longitude: 120.1551, latitude: 30.2741, province: '浙江省' },
  { name: '宁波', longitude: 121.5440, latitude: 29.8683, province: '浙江省' },
  { name: '温州', longitude: 120.6994, latitude: 28.0006, province: '浙江省' },
  { name: '嘉兴', longitude: 120.7555, latitude: 30.7462, province: '浙江省' },
  { name: '湖州', longitude: 120.0873, latitude: 30.8941, province: '浙江省' },
  { name: '绍兴', longitude: 120.5820, latitude: 30.0291, province: '浙江省' },
  { name: '金华', longitude: 119.6491, latitude: 29.0789, province: '浙江省' },
  { name: '衢州', longitude: 118.8595, latitude: 28.9700, province: '浙江省' },
  { name: '舟山', longitude: 122.2070, latitude: 30.0160, province: '浙江省' },
  { name: '台州', longitude: 121.4205, latitude: 28.6568, province: '浙江省' },
  { name: '丽水', longitude: 119.9222, latitude: 28.4517, province: '浙江省' },

  // 安徽省
  { name: '合肥', longitude: 117.2272, latitude: 31.8206, province: '安徽省' },
  { name: '芜湖', longitude: 118.3557, latitude: 31.3260, province: '安徽省' },
  { name: '蚌埠', longitude: 117.3888, latitude: 32.9165, province: '安徽省' },
  { name: '淮南', longitude: 117.0180, latitude: 32.6292, province: '安徽省' },
  { name: '马鞍山', longitude: 118.5063, latitude: 31.6704, province: '安徽省' },
  { name: '淮北', longitude: 116.7984, latitude: 33.9600, province: '安徽省' },
  { name: '铜陵', longitude: 117.8117, latitude: 30.9456, province: '安徽省' },
  { name: '安庆', longitude: 117.0432, latitude: 30.5378, province: '安徽省' },
  { name: '黄山', longitude: 118.3378, latitude: 29.7144, province: '安徽省' },
  { name: '滁州', longitude: 118.3163, latitude: 32.2558, province: '安徽省' },
  { name: '阜阳', longitude: 115.8142, latitude: 32.8969, province: '安徽省' },
  { name: '宿州', longitude: 116.9637, latitude: 33.6333, province: '安徽省' },
  { name: '六安', longitude: 116.5079, latitude: 31.7339, province: '安徽省' },
  { name: '亳州', longitude: 115.7783, latitude: 33.8449, province: '安徽省' },
  { name: '池州', longitude: 117.4894, latitude: 30.6642, province: '安徽省' },
  { name: '宣城', longitude: 118.7585, latitude: 30.9407, province: '安徽省' },

  // 福建省
  { name: '福州', longitude: 119.2965, latitude: 26.0745, province: '福建省' },
  { name: '厦门', longitude: 118.0894, latitude: 24.4798, province: '福建省' },
  { name: '莆田', longitude: 119.0077, latitude: 25.4541, province: '福建省' },
  { name: '三明', longitude: 117.6388, latitude: 26.2634, province: '福建省' },
  { name: '泉州', longitude: 118.6754, latitude: 24.8740, province: '福建省' },
  { name: '漳州', longitude: 117.6472, latitude: 24.5109, province: '福建省' },
  { name: '南平', longitude: 118.1770, latitude: 26.6417, province: '福建省' },
  { name: '龙岩', longitude: 117.0174, latitude: 25.0758, province: '福建省' },
  { name: '宁德', longitude: 119.5479, latitude: 26.6565, province: '福建省' },

  // 江西省
  { name: '南昌', longitude: 115.8581, latitude: 28.6832, province: '江西省' },
  { name: '景德镇', longitude: 117.1782, latitude: 29.2689, province: '江西省' },
  { name: '萍乡', longitude: 113.8548, latitude: 27.6229, province: '江西省' },
  { name: '九江', longitude: 115.9858, latitude: 29.7052, province: '江西省' },
  { name: '新余', longitude: 114.9167, latitude: 27.8178, province: '江西省' },
  { name: '鹰潭', longitude: 117.0692, latitude: 28.2386, province: '江西省' },
  { name: '赣州', longitude: 114.9335, latitude: 25.8310, province: '江西省' },
  { name: '吉安', longitude: 114.9926, latitude: 27.1138, province: '江西省' },
  { name: '宜春', longitude: 114.4166, latitude: 27.8153, province: '江西省' },
  { name: '抚州', longitude: 116.3581, latitude: 27.9492, province: '江西省' },
  { name: '上饶', longitude: 117.9434, latitude: 28.4444, province: '江西省' },

  // 山东省
  { name: '济南', longitude: 117.1205, latitude: 36.6519, province: '山东省' },
  { name: '青岛', longitude: 120.3826, latitude: 36.0671, province: '山东省' },
  { name: '淄博', longitude: 118.0548, latitude: 36.8131, province: '山东省' },
  { name: '枣庄', longitude: 117.3231, latitude: 34.8107, province: '山东省' },
  { name: '东营', longitude: 118.6748, latitude: 37.4337, province: '山东省' },
  { name: '烟台', longitude: 121.4478, latitude: 37.4638, province: '山东省' },
  { name: '潍坊', longitude: 119.1619, latitude: 36.7067, province: '山东省' },
  { name: '济宁', longitude: 116.5871, latitude: 35.4154, province: '山东省' },
  { name: '泰安', longitude: 117.0870, latitude: 36.2003, province: '山东省' },
  { name: '威海', longitude: 122.1203, latitude: 37.5137, province: '山东省' },
  { name: '日照', longitude: 119.5267, latitude: 35.4164, province: '山东省' },
  { name: '临沂', longitude: 118.3564, latitude: 35.1045, province: '山东省' },
  { name: '德州', longitude: 116.3594, latitude: 37.4341, province: '山东省' },
  { name: '聊城', longitude: 115.9858, latitude: 36.4570, province: '山东省' },
  { name: '滨州', longitude: 117.9708, latitude: 37.3835, province: '山东省' },
  { name: '菏泽', longitude: 115.4806, latitude: 35.2333, province: '山东省' },

  // 河南省
  { name: '郑州', longitude: 113.6254, latitude: 34.7466, province: '河南省' },
  { name: '开封', longitude: 114.3077, latitude: 34.7972, province: '河南省' },
  { name: '洛阳', longitude: 112.4538, latitude: 34.6197, province: '河南省' },
  { name: '平顶山', longitude: 113.1928, latitude: 33.7350, province: '河南省' },
  { name: '安阳', longitude: 114.3929, latitude: 36.0970, province: '河南省' },
  { name: '鹤壁', longitude: 114.2972, latitude: 35.7481, province: '河南省' },
  { name: '新乡', longitude: 113.9268, latitude: 35.3030, province: '河南省' },
  { name: '焦作', longitude: 113.2418, latitude: 35.2158, province: '河南省' },
  { name: '濮阳', longitude: 115.0294, latitude: 35.7617, province: '河南省' },
  { name: '许昌', longitude: 113.8525, latitude: 34.0356, province: '河南省' },
  { name: '漯河', longitude: 114.0168, latitude: 33.5758, province: '河南省' },
  { name: '三门峡', longitude: 111.1816, latitude: 34.7730, province: '河南省' },
  { name: '南阳', longitude: 112.5283, latitude: 32.9904, province: '河南省' },
  { name: '商丘', longitude: 115.6566, latitude: 34.4143, province: '河南省' },
  { name: '信阳', longitude: 114.0918, latitude: 32.1470, province: '河南省' },
  { name: '周口', longitude: 114.6496, latitude: 33.6251, province: '河南省' },
  { name: '驻马店', longitude: 114.0220, latitude: 32.9801, province: '河南省' },
  { name: '济源', longitude: 112.6023, latitude: 35.0690, province: '河南省' },

  // 湖北省
  { name: '武汉', longitude: 114.3055, latitude: 30.5931, province: '湖北省' },
  { name: '黄石', longitude: 115.0387, latitude: 30.1993, province: '湖北省' },
  { name: '十堰', longitude: 110.7989, latitude: 32.6291, province: '湖北省' },
  { name: '宜昌', longitude: 111.2858, latitude: 30.6920, province: '湖北省' },
  { name: '襄阳', longitude: 112.1226, latitude: 32.0090, province: '湖北省' },
  { name: '鄂州', longitude: 114.8949, latitude: 30.3917, province: '湖北省' },
  { name: '荆门', longitude: 112.1997, latitude: 31.0354, province: '湖北省' },
  { name: '孝感', longitude: 113.9169, latitude: 30.9265, province: '湖北省' },
  { name: '荆州', longitude: 112.2396, latitude: 30.3353, province: '湖北省' },
  { name: '黄冈', longitude: 114.8727, latitude: 30.4536, province: '湖北省' },
  { name: '咸宁', longitude: 114.3222, latitude: 29.8413, province: '湖北省' },
  { name: '随州', longitude: 113.3823, latitude: 31.6897, province: '湖北省' },
  { name: '恩施', longitude: 109.4869, latitude: 30.2729, province: '湖北省' },
  { name: '仙桃', longitude: 113.4539, latitude: 30.3649, province: '湖北省' },
  { name: '潜江', longitude: 112.8969, latitude: 30.4212, province: '湖北省' },
  { name: '天门', longitude: 113.1665, latitude: 30.6634, province: '湖北省' },
  { name: '神农架', longitude: 110.6749, latitude: 31.7444, province: '湖北省' },

  // 湖南省
  { name: '长沙', longitude: 112.9388, latitude: 28.2282, province: '湖南省' },
  { name: '株洲', longitude: 113.1339, latitude: 27.8273, province: '湖南省' },
  { name: '湘潭', longitude: 112.9443, latitude: 27.8294, province: '湖南省' },
  { name: '衡阳', longitude: 112.5717, latitude: 26.8935, province: '湖南省' },
  { name: '邵阳', longitude: 111.4677, latitude: 27.2389, province: '湖南省' },
  { name: '岳阳', longitude: 113.1288, latitude: 29.3570, province: '湖南省' },
  { name: '常德', longitude: 111.6987, latitude: 29.0319, province: '湖南省' },
  { name: '张家界', longitude: 110.4791, latitude: 29.1273, province: '湖南省' },
  { name: '益阳', longitude: 112.3550, latitude: 28.5536, province: '湖南省' },
  { name: '郴州', longitude: 113.0144, latitude: 25.7705, province: '湖南省' },
  { name: '永州', longitude: 111.6132, latitude: 26.4205, province: '湖南省' },
  { name: '怀化', longitude: 109.9989, latitude: 27.5501, province: '湖南省' },
  { name: '娄底', longitude: 111.9937, latitude: 27.6990, province: '湖南省' },
  { name: '湘西', longitude: 109.7389, latitude: 28.3115, province: '湖南省' },

  // 广东省
  { name: '广州', longitude: 113.2644, latitude: 23.1291, province: '广东省' },
  { name: '韶关', longitude: 113.5977, latitude: 24.8101, province: '广东省' },
  { name: '深圳', longitude: 114.0579, latitude: 22.5431, province: '广东省' },
  { name: '珠海', longitude: 113.5765, latitude: 22.2710, province: '广东省' },
  { name: '汕头', longitude: 116.6819, latitude: 23.3540, province: '广东省' },
  { name: '佛山', longitude: 113.1220, latitude: 23.0217, province: '广东省' },
  { name: '江门', longitude: 113.0819, latitude: 22.5789, province: '广东省' },
  { name: '湛江', longitude: 110.3577, latitude: 21.2707, province: '广东省' },
  { name: '茂名', longitude: 110.9253, latitude: 21.6631, province: '广东省' },
  { name: '肇庆', longitude: 112.4650, latitude: 23.0475, province: '广东省' },
  { name: '惠州', longitude: 114.4152, latitude: 23.1115, province: '广东省' },
  { name: '梅州', longitude: 116.1226, latitude: 24.2888, province: '广东省' },
  { name: '汕尾', longitude: 115.3645, latitude: 22.7745, province: '广东省' },
  { name: '河源', longitude: 114.7004, latitude: 23.7435, province: '广东省' },
  { name: '阳江', longitude: 111.9827, latitude: 21.8579, province: '广东省' },
  { name: '清远', longitude: 113.0565, latitude: 23.6819, province: '广东省' },
  { name: '东莞', longitude: 113.7518, latitude: 23.0205, province: '广东省' },
  { name: '中山', longitude: 113.3927, latitude: 22.5171, province: '广东省' },
  { name: '潮州', longitude: 116.6229, latitude: 23.6566, province: '广东省' },
  { name: '揭阳', longitude: 116.3729, latitude: 23.5498, province: '广东省' },
  { name: '云浮', longitude: 112.0444, latitude: 22.9151, province: '广东省' },

  // 台湾省
  { name: '台北', longitude: 121.5654, latitude: 25.0330, province: '中国台湾' },
  { name: '新北', longitude: 121.4657, latitude: 25.0114, province: '中国台湾' },
  { name: '桃园', longitude: 121.3000, latitude: 24.9936, province: '中国台湾' },
  { name: '台中', longitude: 120.6849, latitude: 24.1477, province: '中国台湾' },
  { name: '台南', longitude: 120.2133, latitude: 22.9908, province: '中国台湾' },
  { name: '高雄', longitude: 120.3014, latitude: 22.6273, province: '中国台湾' },
  { name: '基隆', longitude: 121.7469, latitude: 25.1276, province: '中国台湾' },
  { name: '新竹', longitude: 120.9688, latitude: 24.8067, province: '中国台湾' },
  { name: '嘉义', longitude: 120.4535, latitude: 23.4801, province: '中国台湾' },

  // 广西壮族自治区
  { name: '南宁', longitude: 108.3661, latitude: 22.8172, province: '广西壮族自治区' },
  { name: '柳州', longitude: 109.4281, latitude: 24.3265, province: '广西壮族自治区' },
  { name: '桂林', longitude: 110.2903, latitude: 25.2739, province: '广西壮族自治区' },
  { name: '梧州', longitude: 111.2795, latitude: 23.4772, province: '广西壮族自治区' },
  { name: '北海', longitude: 109.1207, latitude: 21.4814, province: '广西壮族自治区' },
  { name: '防城港', longitude: 108.3548, latitude: 21.6868, province: '广西壮族自治区' },
  { name: '钦州', longitude: 108.6543, latitude: 21.9797, province: '广西壮族自治区' },
  { name: '贵港', longitude: 109.5986, latitude: 23.1113, province: '广西壮族自治区' },
  { name: '玉林', longitude: 110.1806, latitude: 22.6542, province: '广西壮族自治区' },
  { name: '百色', longitude: 106.6183, latitude: 23.9022, province: '广西壮族自治区' },
  { name: '贺州', longitude: 111.5521, latitude: 24.4037, province: '广西壮族自治区' },
  { name: '河池', longitude: 108.0852, latitude: 24.6928, province: '广西壮族自治区' },
  { name: '来宾', longitude: 109.2216, latitude: 23.7509, province: '广西壮族自治区' },
  { name: '崇左', longitude: 107.3645, latitude: 22.3763, province: '广西壮族自治区' },

  // 海南省
  { name: '海口', longitude: 110.1999, latitude: 20.0440, province: '海南省' },
  { name: '三亚', longitude: 109.5082, latitude: 18.2528, province: '海南省' },
  { name: '三沙', longitude: 112.3486, latitude: 16.8311, province: '海南省' },
  { name: '儋州', longitude: 109.5767, latitude: 19.5211, province: '海南省' },
  { name: '五指山', longitude: 109.5167, latitude: 18.7751, province: '海南省' },
  { name: '琼海', longitude: 110.4746, latitude: 19.2584, province: '海南省' },
  { name: '文昌', longitude: 110.7977, latitude: 19.5435, province: '海南省' },
  { name: '万宁', longitude: 110.3893, latitude: 18.7951, province: '海南省' },
  { name: '东方', longitude: 108.6538, latitude: 19.1019, province: '海南省' },

  // 四川省
  { name: '成都', longitude: 104.0668, latitude: 30.5728, province: '四川省' },
  { name: '自贡', longitude: 104.7787, latitude: 29.3391, province: '四川省' },
  { name: '攀枝花', longitude: 101.7185, latitude: 26.5804, province: '四川省' },
  { name: '泸州', longitude: 105.4425, latitude: 28.8714, province: '四川省' },
  { name: '德阳', longitude: 104.3979, latitude: 31.1266, province: '四川省' },
  { name: '绵阳', longitude: 104.6791, latitude: 31.4670, province: '四川省' },
  { name: '广元', longitude: 105.8439, latitude: 32.4353, province: '四川省' },
  { name: '遂宁', longitude: 105.5928, latitude: 30.5328, province: '四川省' },
  { name: '内江', longitude: 105.0584, latitude: 29.5806, province: '四川省' },
  { name: '乐山', longitude: 103.7657, latitude: 29.5520, province: '四川省' },
  { name: '南充', longitude: 106.1105, latitude: 30.8378, province: '四川省' },
  { name: '眉山', longitude: 103.8485, latitude: 30.0756, province: '四川省' },
  { name: '宜宾', longitude: 104.6430, latitude: 28.7696, province: '四川省' },
  { name: '广安', longitude: 106.6333, latitude: 30.4564, province: '四川省' },
  { name: '达州', longitude: 107.4675, latitude: 31.2096, province: '四川省' },
  { name: '雅安', longitude: 103.0420, latitude: 29.9803, province: '四川省' },
  { name: '巴中', longitude: 106.7536, latitude: 31.8691, province: '四川省' },
  { name: '资阳', longitude: 104.6419, latitude: 30.1222, province: '四川省' },
  { name: '阿坝', longitude: 102.2243, latitude: 31.8998, province: '四川省' },
  { name: '甘孜', longitude: 101.9638, latitude: 30.0498, province: '四川省' },
  { name: '凉山', longitude: 102.2587, latitude: 27.8808, province: '四川省' },

  // 贵州省
  { name: '贵阳', longitude: 106.6302, latitude: 26.6477, province: '贵州省' },
  { name: '六盘水', longitude: 104.8305, latitude: 26.5846, province: '贵州省' },
  { name: '遵义', longitude: 106.9275, latitude: 27.7256, province: '贵州省' },
  { name: '安顺', longitude: 105.9462, latitude: 26.2455, province: '贵州省' },
  { name: '毕节', longitude: 105.2889, latitude: 27.2849, province: '贵州省' },
  { name: '铜仁', longitude: 109.1896, latitude: 27.6865, province: '贵州省' },
  { name: '黔西南', longitude: 104.9006, latitude: 25.0881, province: '贵州省' },
  { name: '黔东南', longitude: 107.9775, latitude: 26.5833, province: '贵州省' },
  { name: '黔南', longitude: 107.5173, latitude: 26.2533, province: '贵州省' },

  // 云南省
  { name: '昆明', longitude: 102.8329, latitude: 24.8801, province: '云南省' },
  { name: '曲靖', longitude: 103.7974, latitude: 25.4899, province: '云南省' },
  { name: '玉溪', longitude: 102.5431, latitude: 24.3505, province: '云南省' },
  { name: '保山', longitude: 99.1670, latitude: 25.1119, province: '云南省' },
  { name: '昭通', longitude: 103.7176, latitude: 27.3380, province: '云南省' },
  { name: '丽江', longitude: 100.2270, latitude: 26.8721, province: '云南省' },
  { name: '普洱', longitude: 100.9726, latitude: 22.7775, province: '云南省' },
  { name: '临沧', longitude: 100.0885, latitude: 23.8839, province: '云南省' },
  { name: '楚雄', longitude: 101.5282, latitude: 25.0410, province: '云南省' },
  { name: '红河', longitude: 103.3743, latitude: 23.3633, province: '云南省' },
  { name: '文山', longitude: 104.2169, latitude: 23.3695, province: '云南省' },
  { name: '西双版纳', longitude: 100.7977, latitude: 22.0017, province: '云南省' },
  { name: '大理', longitude: 100.2675, latitude: 25.5969, province: '云南省' },
  { name: '德宏', longitude: 98.5784, latitude: 24.4332, province: '云南省' },
  { name: '怒江', longitude: 98.8567, latitude: 25.8179, province: '云南省' },
  { name: '迪庆', longitude: 99.7065, latitude: 27.8193, province: '云南省' },

  // 西藏自治区
  { name: '拉萨', longitude: 91.1320, latitude: 29.6600, province: '西藏自治区' },
  { name: '日喀则', longitude: 88.8851, latitude: 29.2690, province: '西藏自治区' },
  { name: '昌都', longitude: 97.1785, latitude: 31.1369, province: '西藏自治区' },
  { name: '林芝', longitude: 94.3617, latitude: 29.6491, province: '西藏自治区' },
  { name: '山南', longitude: 91.7730, latitude: 29.2370, province: '西藏自治区' },
  { name: '那曲', longitude: 92.0535, latitude: 31.4760, province: '西藏自治区' },
  { name: '阿里', longitude: 80.1055, latitude: 32.5012, province: '西藏自治区' },

  // 陕西省
  { name: '西安', longitude: 108.9398, latitude: 34.3416, province: '陕西省' },
  { name: '铜川', longitude: 108.9450, latitude: 34.8969, province: '陕西省' },
  { name: '宝鸡', longitude: 107.2372, latitude: 34.3609, province: '陕西省' },
  { name: '咸阳', longitude: 108.7093, latitude: 34.3297, province: '陕西省' },
  { name: '渭南', longitude: 109.5097, latitude: 34.4995, province: '陕西省' },
  { name: '延安', longitude: 109.4897, latitude: 36.5851, province: '陕西省' },
  { name: '汉中', longitude: 107.0234, latitude: 33.0675, province: '陕西省' },
  { name: '榆林', longitude: 109.7341, latitude: 38.2854, province: '陕西省' },
  { name: '安康', longitude: 109.0295, latitude: 32.6852, province: '陕西省' },
  { name: '商洛', longitude: 109.9404, latitude: 33.8683, province: '陕西省' },

  // 甘肃省
  { name: '兰州', longitude: 103.8343, latitude: 36.0611, province: '甘肃省' },
  { name: '嘉峪关', longitude: 98.2894, latitude: 39.7720, province: '甘肃省' },
  { name: '金昌', longitude: 102.1878, latitude: 38.5205, province: '甘肃省' },
  { name: '白银', longitude: 104.1389, latitude: 36.5447, province: '甘肃省' },
  { name: '天水', longitude: 105.7244, latitude: 34.5808, province: '甘肃省' },
  { name: '武威', longitude: 102.6381, latitude: 37.9282, province: '甘肃省' },
  { name: '张掖', longitude: 100.4497, latitude: 38.9252, province: '甘肃省' },
  { name: '平凉', longitude: 106.6650, latitude: 35.5428, province: '甘肃省' },
  { name: '酒泉', longitude: 98.4938, latitude: 39.7331, province: '甘肃省' },
  { name: '庆阳', longitude: 107.6437, latitude: 35.7095, province: '甘肃省' },
  { name: '定西', longitude: 104.5860, latitude: 35.5806, province: '甘肃省' },
  { name: '陇南', longitude: 104.9216, latitude: 33.4008, province: '甘肃省' },
  { name: '临夏', longitude: 103.2106, latitude: 35.5992, province: '甘肃省' },
  { name: '甘南', longitude: 102.9110, latitude: 34.9837, province: '甘肃省' },

  // 青海省
  { name: '西宁', longitude: 101.7782, latitude: 36.6171, province: '青海省' },
  { name: '海东', longitude: 102.1040, latitude: 36.5029, province: '青海省' },
  { name: '海北', longitude: 100.9010, latitude: 36.9542, province: '青海省' },
  { name: '黄南', longitude: 102.0076, latitude: 35.5177, province: '青海省' },
  { name: '海南', longitude: 100.6196, latitude: 36.2804, province: '青海省' },
  { name: '果洛', longitude: 100.2420, latitude: 34.4736, province: '青海省' },
  { name: '玉树', longitude: 97.0085, latitude: 33.0044, province: '青海省' },
  { name: '海西', longitude: 97.3708, latitude: 37.3747, province: '青海省' },

  // 宁夏回族自治区
  { name: '银川', longitude: 106.2586, latitude: 38.4872, province: '宁夏回族自治区' },
  { name: '石嘴山', longitude: 106.3839, latitude: 39.0133, province: '宁夏回族自治区' },
  { name: '吴忠', longitude: 106.1987, latitude: 37.9974, province: '宁夏回族自治区' },
  { name: '固原', longitude: 106.2426, latitude: 36.0159, province: '宁夏回族自治区' },
  { name: '中卫', longitude: 105.1899, latitude: 37.4993, province: '宁夏回族自治区' },

  // 新疆维吾尔自治区
  { name: '乌鲁木齐', longitude: 87.6168, latitude: 43.8256, province: '新疆维吾尔自治区' },
  { name: '克拉玛依', longitude: 84.8890, latitude: 45.5795, province: '新疆维吾尔自治区' },
  { name: '吐鲁番', longitude: 89.1815, latitude: 42.9513, province: '新疆维吾尔自治区' },
  { name: '哈密', longitude: 93.5155, latitude: 42.8185, province: '新疆维吾尔自治区' },
  { name: '昌吉', longitude: 87.3040, latitude: 44.0114, province: '新疆维吾尔自治区' },
  { name: '博尔塔拉', longitude: 82.0745, latitude: 44.9058, province: '新疆维吾尔自治区' },
  { name: '巴音郭楞', longitude: 86.1452, latitude: 41.7686, province: '新疆维吾尔自治区' },
  { name: '阿克苏', longitude: 80.2651, latitude: 41.1707, province: '新疆维吾尔自治区' },
  { name: '克孜勒苏', longitude: 76.1674, latitude: 39.7145, province: '新疆维吾尔自治区' },
  { name: '喀什', longitude: 75.9893, latitude: 39.4677, province: '新疆维吾尔自治区' },
  { name: '和田', longitude: 79.9224, latitude: 37.1105, province: '新疆维吾尔自治区' },
  { name: '伊犁', longitude: 81.3248, latitude: 43.9171, province: '新疆维吾尔自治区' },
  { name: '塔城', longitude: 82.9804, latitude: 46.7456, province: '新疆维吾尔自治区' },
  { name: '阿勒泰', longitude: 88.1396, latitude: 47.8449, province: '新疆维吾尔自治区' },

  // 新疆生产建设兵团（师市）
  { name: '石河子', longitude: 86.0411, latitude: 44.3059, province: '新疆生产建设兵团' },
  { name: '阿拉尔', longitude: 81.2805, latitude: 40.5477, province: '新疆生产建设兵团' },
  { name: '图木舒克', longitude: 79.0749, latitude: 39.8673, province: '新疆生产建设兵团' },
  { name: '五家渠', longitude: 87.5269, latitude: 44.1674, province: '新疆生产建设兵团' },
  { name: '北屯', longitude: 87.8085, latitude: 47.3623, province: '新疆生产建设兵团' },
  { name: '铁门关', longitude: 85.6845, latitude: 41.8687, province: '新疆生产建设兵团' },
  { name: '双河', longitude: 82.3537, latitude: 44.8405, province: '新疆生产建设兵团' },
  { name: '可克达拉', longitude: 80.9942, latitude: 43.9387, province: '新疆生产建设兵团' },
  { name: '昆玉', longitude: 79.2864, latitude: 37.2095, province: '新疆生产建设兵团' },
  { name: '胡杨河', longitude: 84.8276, latitude: 44.6929, province: '新疆生产建设兵团' },
  { name: '新星', longitude: 93.5150, latitude: 42.8250, province: '新疆生产建设兵团' },
  { name: '白杨', longitude: 81.0153, latitude: 40.5234, province: '新疆生产建设兵团' },

  // 香港特别行政区
  { name: '香港', longitude: 114.1694, latitude: 22.3193, province: '中国香港' },
  { name: '九龙', longitude: 114.1779, latitude: 22.3068, province: '中国香港' },
  { name: '新界', longitude: 114.2029, latitude: 22.3793, province: '中国香港' },

  // 澳门特别行政区
  { name: '澳门', longitude: 113.5439, latitude: 22.1987, province: '中国澳门' },
  { name: '氹仔', longitude: 113.5770, latitude: 22.1589, province: '中国澳门' },
  { name: '路环', longitude: 113.5622, latitude: 22.1133, province: '中国澳门' },

  // 新加坡
  { name: '新加坡', longitude: 103.8198, latitude: 1.3521, province: '新加坡' },
  { name: '裕廊', longitude: 103.7061, latitude: 1.3324, province: '新加坡' },
  { name: '淡滨尼', longitude: 103.9454, latitude: 1.3528, province: '新加坡' },

  // 马来西亚
  { name: '吉隆坡', longitude: 101.6869, latitude: 3.1390, province: '马来西亚' },
  { name: '槟城', longitude: 100.3327, latitude: 5.4164, province: '马来西亚' },
  { name: '新山', longitude: 103.7618, latitude: 1.4927, province: '马来西亚' },
  { name: '马六甲', longitude: 102.2501, latitude: 2.2008, province: '马来西亚' },
  { name: '怡保', longitude: 101.0820, latitude: 4.5975, province: '马来西亚' },
  { name: '亚庇', longitude: 116.0735, latitude: 5.9804, province: '马来西亚' },
  { name: '古晋', longitude: 110.3593, latitude: 1.5533, province: '马来西亚' },

  // 泰国
  { name: '曼谷', longitude: 100.5018, latitude: 13.7563, province: '泰国' },
  { name: '清迈', longitude: 98.9853, latitude: 18.7883, province: '泰国' },
  { name: '普吉', longitude: 98.3981, latitude: 7.8804, province: '泰国' },
  { name: '芭提雅', longitude: 100.8778, latitude: 12.9236, province: '泰国' },
  { name: '合艾', longitude: 100.4762, latitude: 7.0086, province: '泰国' },
  { name: '孔敬', longitude: 102.8350, latitude: 16.4419, province: '泰国' },
  { name: '华欣', longitude: 99.9579, latitude: 12.5684, province: '泰国' },

  // 日本
  { name: '东京', longitude: 139.6917, latitude: 35.6895, province: '日本' },
  { name: '大阪', longitude: 135.5023, latitude: 34.6937, province: '日本' },
  { name: '京都', longitude: 135.7681, latitude: 35.0116, province: '日本' },
  { name: '横滨', longitude: 139.6380, latitude: 35.4437, province: '日本' },
  { name: '名古屋', longitude: 136.9066, latitude: 35.1815, province: '日本' },
  { name: '札幌', longitude: 141.3545, latitude: 43.0618, province: '日本' },
  { name: '福冈', longitude: 130.4017, latitude: 33.5904, province: '日本' },
  { name: '仙台', longitude: 140.8719, latitude: 38.2682, province: '日本' },
  { name: '广岛', longitude: 132.4553, latitude: 34.3853, province: '日本' },
  { name: '冲绳', longitude: 127.6792, latitude: 26.2124, province: '日本' },

  // 韩国
  { name: '首尔', longitude: 126.9780, latitude: 37.5665, province: '韩国' },
  { name: '釜山', longitude: 129.0756, latitude: 35.1796, province: '韩国' },
  { name: '仁川', longitude: 126.7052, latitude: 37.4563, province: '韩国' },
  { name: '大邱', longitude: 128.6014, latitude: 35.8714, province: '韩国' },
  { name: '大田', longitude: 127.3845, latitude: 36.3504, province: '韩国' },
  { name: '光州', longitude: 126.8526, latitude: 35.1595, province: '韩国' },
  { name: '济州', longitude: 126.5312, latitude: 33.4996, province: '韩国' },

  // 越南
  { name: '河内', longitude: 105.8342, latitude: 21.0278, province: '越南' },
  { name: '海防', longitude: 106.6881, latitude: 20.8449, province: '越南' },
  { name: '岘港', longitude: 108.2022, latitude: 16.0544, province: '越南' },
  { name: '顺化', longitude: 107.5909, latitude: 16.4637, province: '越南' },
  { name: '芽庄', longitude: 109.1967, latitude: 12.2388, province: '越南' },

  // 菲律宾
  { name: '马尼拉', longitude: 120.9842, latitude: 14.5995, province: '菲律宾' },
  { name: '奎松市', longitude: 121.0437, latitude: 14.6760, province: '菲律宾' },
  { name: '宿务', longitude: 123.8854, latitude: 10.3157, province: '菲律宾' },
  { name: '达沃', longitude: 125.6128, latitude: 7.1907, province: '菲律宾' },

  // 印度
  { name: '新德里', longitude: 77.2090, latitude: 28.6139, province: '印度' },
  { name: '孟买', longitude: 72.8777, latitude: 19.0760, province: '印度' },
  { name: '加尔各答', longitude: 88.3639, latitude: 22.5726, province: '印度' },
  { name: '班加罗尔', longitude: 77.5946, latitude: 12.9716, province: '印度' },
  { name: '金奈', longitude: 80.2707, latitude: 13.0827, province: '印度' },
  { name: '海得拉巴', longitude: 78.4867, latitude: 17.3850, province: '印度' },

  // 阿联酋
  { name: '迪拜', longitude: 55.2708, latitude: 25.2048, province: '阿联酋' },
  { name: '阿布扎比', longitude: 54.3773, latitude: 24.4539, province: '阿联酋' },
  { name: '沙迦', longitude: 55.4033, latitude: 25.3463, province: '阿联酋' },

  // 沙特阿拉伯
  { name: '利雅得', longitude: 46.6753, latitude: 24.7136, province: '沙特阿拉伯' },
  { name: '吉达', longitude: 39.1925, latitude: 21.4858, province: '沙特阿拉伯' },
  { name: '麦加', longitude: 39.8579, latitude: 21.3891, province: '沙特阿拉伯' },
  { name: '麦地那', longitude: 39.6111, latitude: 24.5247, province: '沙特阿拉伯' },

  // 土耳其
  { name: '伊斯坦布尔', longitude: 28.9784, latitude: 41.0082, province: '土耳其' },
  { name: '安卡拉', longitude: 32.8597, latitude: 39.9334, province: '土耳其' },
  { name: '伊兹密尔', longitude: 27.1428, latitude: 38.4237, province: '土耳其' },
  { name: '安塔利亚', longitude: 30.7133, latitude: 36.8969, province: '土耳其' },

  // 俄罗斯
  { name: '莫斯科', longitude: 37.6173, latitude: 55.7558, province: '俄罗斯' },
  { name: '圣彼得堡', longitude: 30.3351, latitude: 59.9343, province: '俄罗斯' },
  { name: '符拉迪沃斯托克', longitude: 131.8855, latitude: 43.1155, province: '俄罗斯' },
  { name: '新西伯利亚', longitude: 82.9204, latitude: 55.0302, province: '俄罗斯' },

  // 美国
  { name: '纽约', longitude: -74.0060, latitude: 40.7128, province: '美国' },
  { name: '洛杉矶', longitude: -118.2437, latitude: 34.0522, province: '美国' },
  { name: '旧金山', longitude: -122.4194, latitude: 37.7749, province: '美国' },
  { name: '西雅图', longitude: -122.3321, latitude: 47.6062, province: '美国' },
  { name: '芝加哥', longitude: -87.6298, latitude: 41.8781, province: '美国' },
  { name: '波士顿', longitude: -71.0589, latitude: 42.3601, province: '美国' },
  { name: '华盛顿', longitude: -77.0369, latitude: 38.9072, province: '美国' },
  { name: '休斯敦', longitude: -95.3698, latitude: 29.7604, province: '美国' },
  { name: '达拉斯', longitude: -96.7970, latitude: 32.7767, province: '美国' },
  { name: '迈阿密', longitude: -80.1918, latitude: 25.7617, province: '美国' },
  { name: '檀香山', longitude: -157.8583, latitude: 21.3069, province: '美国' },

  // 加拿大
  { name: '温哥华', longitude: -123.1207, latitude: 49.2827, province: '加拿大' },
  { name: '多伦多', longitude: -79.3832, latitude: 43.6532, province: '加拿大' },
  { name: '蒙特利尔', longitude: -73.5673, latitude: 45.5017, province: '加拿大' },
  { name: '渥太华', longitude: -75.6972, latitude: 45.4215, province: '加拿大' },
  { name: '卡尔加里', longitude: -114.0719, latitude: 51.0447, province: '加拿大' },

  // 英国
  { name: '伦敦', longitude: -0.1276, latitude: 51.5074, province: '英国' },
  { name: '曼彻斯特', longitude: -2.2426, latitude: 53.4808, province: '英国' },
  { name: '伯明翰', longitude: -1.8904, latitude: 52.4862, province: '英国' },
  { name: '爱丁堡', longitude: -3.1883, latitude: 55.9533, province: '英国' },
  { name: '格拉斯哥', longitude: -4.2518, latitude: 55.8642, province: '英国' },

  // 法国
  { name: '巴黎', longitude: 2.3522, latitude: 48.8566, province: '法国' },
  { name: '里昂', longitude: 4.8357, latitude: 45.7640, province: '法国' },
  { name: '马赛', longitude: 5.3698, latitude: 43.2965, province: '法国' },
  { name: '尼斯', longitude: 7.2620, latitude: 43.7102, province: '法国' },

  // 德国
  { name: '柏林', longitude: 13.4050, latitude: 52.5200, province: '德国' },
  { name: '慕尼黑', longitude: 11.5820, latitude: 48.1351, province: '德国' },
  { name: '法兰克福', longitude: 8.6821, latitude: 50.1109, province: '德国' },
  { name: '汉堡', longitude: 9.9937, latitude: 53.5511, province: '德国' },
  { name: '科隆', longitude: 6.9603, latitude: 50.9375, province: '德国' },

  // 意大利
  { name: '罗马', longitude: 12.4964, latitude: 41.9028, province: '意大利' },
  { name: '米兰', longitude: 9.1900, latitude: 45.4642, province: '意大利' },
  { name: '佛罗伦萨', longitude: 11.2558, latitude: 43.7696, province: '意大利' },
  { name: '威尼斯', longitude: 12.3155, latitude: 45.4408, province: '意大利' },

  // 西班牙
  { name: '马德里', longitude: -3.7038, latitude: 40.4168, province: '西班牙' },
  { name: '巴塞罗那', longitude: 2.1734, latitude: 41.3851, province: '西班牙' },
  { name: '瓦伦西亚', longitude: -0.3763, latitude: 39.4699, province: '西班牙' },
  { name: '塞维利亚', longitude: -5.9845, latitude: 37.3891, province: '西班牙' },

  // 荷兰
  { name: '阿姆斯特丹', longitude: 4.9041, latitude: 52.3676, province: '荷兰' },
  { name: '鹿特丹', longitude: 4.4777, latitude: 51.9244, province: '荷兰' },
  { name: '海牙', longitude: 4.3007, latitude: 52.0705, province: '荷兰' },
];

// 按省份分组
export const CITIES_BY_PROVINCE = CITIES.reduce((acc, city) => {
  if (!acc[city.province]) {
    acc[city.province] = [];
  }
  acc[city.province].push(city);
  return acc;
}, {} as Record<string, City[]>);

const PRIORITY_PROVINCES = [
  '北京市',
  '上海市',
  '广东省',
  '江苏省',
  '浙江省',
  '山东省',
  '河南省',
  '湖北省',
  '湖南省',
  '福建省',
  '四川省',
  '河北省',
  '安徽省',
  '江西省',
  '辽宁省',
  '吉林省',
  '黑龙江省',
  '陕西省',
  '广西壮族自治区',
  '云南省',
  '天津市',
  '重庆市',
  '中国香港',
  '中国澳门',
  '日本',
  '韩国',
  '美国',
  '加拿大',
  '英国',
  '法国',
  '德国',
  '意大利',
  '西班牙',
  '荷兰',
  '俄罗斯',
  '阿联酋',
  '沙特阿拉伯',
  '土耳其',
  '新加坡',
  '马来西亚',
  '泰国',
  '越南',
  '菲律宾',
  '印度',
];

function compareProvince(a: string, b: string) {
  const aPriority = PRIORITY_PROVINCES.indexOf(a);
  const bPriority = PRIORITY_PROVINCES.indexOf(b);

  if (aPriority !== -1 || bPriority !== -1) {
    if (aPriority === -1) return 1;
    if (bPriority === -1) return -1;
    return aPriority - bPriority;
  }

  return a.localeCompare(b, 'zh-CN');
}

// 获取所有省份列表
export const PROVINCES = Object.keys(CITIES_BY_PROVINCE).sort(compareProvince);

export const CITY_OPTIONS: CitySelectGroup[] = PROVINCES.map((province) => ({
  label: province,
  options: CITIES_BY_PROVINCE[province].map((city) => ({
    label: city.name,
    value: getCityOptionValue(city),
    city,
    searchText: `${city.name} ${city.province} ${getCityOptionValue(city)}`.toLowerCase(),
  })),
}));

export const DOMESTIC_CITY_OPTIONS = CITY_OPTIONS.filter((group) => isDomesticProvince(group.label));
export const OVERSEAS_CITY_OPTIONS = CITY_OPTIONS.filter((group) => !isDomesticProvince(group.label));

export function getCityOptionsByLocale(locale: CityLocale, scope: 'domestic' | 'overseas'): CitySelectGroup[] {
  const baseGroups = scope === 'overseas' ? OVERSEAS_CITY_OPTIONS : DOMESTIC_CITY_OPTIONS;

  return baseGroups.map((group) => ({
    label: getLocalizedProvinceName(group.label, locale),
    options: group.options.map((option) => {
      const localizedCity = getLocalizedCityName(option.city.name, locale);
      const localizedProvince = getLocalizedProvinceName(option.city.province, locale);

      return {
        ...option,
        label: localizedCity,
        searchText: [
          option.city.name,
          option.city.province,
          localizedCity,
          localizedProvince,
          option.value,
        ]
          .join(' ')
          .toLowerCase(),
      };
    }),
  }));
}

export function cityFilterOption(input: string, option?: { searchText?: string; label?: string; value?: string }) {
  const keyword = input.trim().toLowerCase();
  if (!keyword) return true;

  const haystack = `${option?.searchText || ''} ${option?.label || ''} ${option?.value || ''}`.toLowerCase();
  return haystack.includes(keyword);
}
